import React, { useState, useEffect } from 'react';
import ProjectTabs from '../components/ProjectTabs';
import LoadingSpinner from '../components/LoadingSpinner';
import { analyzePDFs, analyzeProjectsByName } from '../utils/api';
import './AddProjectPage.css';

const AddProjectPage = () => {
  // Form state
  const [projectName, setProjectName] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  const [loadingTime, setLoadingTime] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // Results state
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Analysis History
  const [analysisHistory, setAnalysisHistory] = useState([]);

  // Loading timer effect
  useEffect(() => {
    let interval;
    if (loading) {
      const startTime = Date.now();
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setLoadingTime(elapsed);
        
        if (pdfFile) {
          if (elapsed < 20) {
            setLoadingMessage('📄 Processing PDF file...');
          } else if (elapsed < 60) {
            setLoadingMessage('🔍 Extracting text and analyzing content...');
          } else if (elapsed < 120) {
            setLoadingMessage('🧠 AI is analyzing project details...');
          } else {
            setLoadingMessage('💾 Creating embeddings and saving to database...');
          }
        } else {
          if (elapsed < 15) {
            setLoadingMessage('🌐 Searching Google for project information...');
          } else if (elapsed < 40) {
            setLoadingMessage('🖼️ Collecting project images...');
          } else if (elapsed < 80) {
            setLoadingMessage('🧠 AI is analyzing and structuring data...');
          } else {
            setLoadingMessage('💾 Saving to database...');
          }
        }
      }, 1000);
    } else {
      setLoadingTime(0);
      setLoadingMessage('');
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading, pdfFile]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!projectName.trim() && !pdfFile) {
      setError('Please enter a project name or upload a PDF');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      let data;
      
      if (pdfFile) {
        data = await analyzePDFs([pdfFile]);
        addToHistory('pdf', data.projects, pdfFile.name);
      } else {
        const response = await analyzeProjectsByName([projectName.trim()]);
        data = response;
        addToHistory('search', data.projects);
      }
      
      setResults(data);
      setProjectName('');
      setPdfFile(null);
      
    } catch (err) {
      console.error('Analysis error:', err);
      let errorMessage = 'An error occurred. Please try again.';
      
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMessage = 'Analysis timed out. Please try again.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again in a few moments.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setError(null);
    } else if (file) {
      setError('Please select a PDF file');
    }
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setError(null);
    } else if (file) {
      setError('Please drop a PDF file');
    }
  };

  // Remove attached PDF
  const removePdf = () => {
    setPdfFile(null);
  };

  // History management
  const addToHistory = (method, projects, filename = null) => {
    if (!projects || projects.length === 0) return;
    
    const newEntry = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      method: method,
      filename: filename,
      projectCount: projects.length,
      projectNames: projects.map(p => p.project_name || 'Unknown')
    };
    
    setAnalysisHistory(prev => [newEntry, ...prev.slice(0, 9)]);
  };

  // Reset form
  const resetForm = () => {
    setProjectName('');
    setPdfFile(null);
    setResults(null);
    setError(null);
  };

  return (
    <div className="add-project-page">
      <div className="page-intro">
        <h2>➕ Add New Project</h2>
        <p>Enter a project name to search online, or attach a PDF brochure for direct extraction</p>
      </div>

      <div className="add-project-form-container">
        <form onSubmit={handleSubmit} className="add-project-form">
          
          <div className="form-section">
            <label htmlFor="projectName">Project Name</label>
            <input
              id="projectName"
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder='e.g., "Hyde Park New Cairo", "Mountain View iCity"'
              disabled={loading}
              className="project-name-input"
            />
          </div>

          <div className="form-section">
            <label>PDF Brochure <span className="optional-label">(Optional)</span></label>
            
            {!pdfFile ? (
              <div
                className={`pdf-dropzone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById('pdfInput').click()}
              >
                <input
                  id="pdfInput"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <span className="dropzone-icon">📄</span>
                <span className="dropzone-text">Drop PDF here or click to browse</span>
                <span className="dropzone-hint">If attached, data will be extracted from PDF instead of web search</span>
              </div>
            ) : (
              <div className="pdf-attached">
                <div className="pdf-info">
                  <span className="pdf-icon">📄</span>
                  <span className="pdf-name">{pdfFile.name}</span>
                  <span className="pdf-size">({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
                <button 
                  type="button" 
                  className="remove-pdf-btn"
                  onClick={removePdf}
                  disabled={loading}
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          <div className="data-source-indicator">
            {pdfFile ? (
              <div className="source-badge pdf-source">
                <span className="badge-icon">📑</span>
                <span>Data will be extracted from PDF</span>
              </div>
            ) : projectName.trim() ? (
              <div className="source-badge web-source">
                <span className="badge-icon">🌐</span>
                <span>Will search Google for project info + images</span>
              </div>
            ) : (
              <div className="source-badge empty-source">
                <span className="badge-icon">💡</span>
                <span>Enter a project name or attach a PDF to get started</span>
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="form-actions">
            {results && (
              <button 
                type="button" 
                className="btn btn-reset"
                onClick={resetForm}
                disabled={loading}
              >
                🔄 Add Another
              </button>
            )}
            <button 
              type="submit" 
              className="btn btn-submit"
              disabled={loading || (!projectName.trim() && !pdfFile)}
            >
              {loading ? `⏳ Analyzing... (${loadingTime}s)` : '➕ Add Project'}
            </button>
          </div>
        </form>

        {loading && (
          <div className="loading-section">
            <LoadingSpinner 
              message={loadingMessage}
              showProgress={loadingTime > 30}
              progress={Math.min((loadingTime / 180) * 100, 95)}
            />
          </div>
        )}
      </div>

      {results && results.projects && results.projects.length > 0 && !loading && (
        <div className="results-section">
          <h3>✅ Project Added Successfully!</h3>
          <p className="results-subtitle">
            {results.projects.length} project{results.projects.length !== 1 ? 's' : ''} extracted and saved to database
          </p>
          <ProjectTabs projects={results.projects} />
        </div>
      )}

      {analysisHistory.length > 0 && (
        <div className="analysis-history">
          <h3>📊 Recent Additions</h3>
          <div className="history-list">
            {analysisHistory.map(entry => (
              <div key={entry.id} className="history-item">
                <div className="history-header">
                  <span className="history-method">
                    {entry.method === 'pdf' ? '📄 PDF' : '🌐 Web Search'}
                  </span>
                  {entry.filename && (
                    <span className="history-filename">{entry.filename}</span>
                  )}
                  <span className="history-timestamp">{entry.timestamp}</span>
                </div>
                <div className="history-projects">
                  {entry.projectNames.map((name, idx) => (
                    <span key={idx} className="project-tag">{name}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProjectPage;
